import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Save, Trash2, MapPin, Phone, Clock, Globe, ExternalLink, Car,
  Eye, EyeOff, Loader2, X, Edit2, ChevronDown, ChevronUp, Navigation, Lock
} from 'lucide-react';
import { API } from '@/App';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWdlbmNlbXNjIiwiYSI6ImNtbmN6NGN6ajEyNjYycnNoMnhoZnB2cm4ifQ.DR23BWxFiKaOpi3fHH06mQ';

const REGIONS = [
  'Île-de-France', 'Auvergne-Rhône-Alpes', 'Nouvelle-Aquitaine', 'Occitanie',
  'Hauts-de-France', 'Provence-Alpes-Côte d\'Azur', 'Grand Est', 'Pays de la Loire',
  'Normandie', 'Bretagne', 'Bourgogne-Franche-Comté', 'Centre-Val de Loire', 'Corse'
];

const SPECIALTIES_SUGGESTIONS = [
  'Lentilles de contact', 'Contactologie', 'Kératocône', 'Lentilles sclérales',
  'Lentilles rigides', 'Lentilles souples', 'Orthokératologie', 'Presbytie',
  'Sécheresse oculaire', 'Adaptation lentilles', 'Lentilles pédiatriques'
];

const emptyFiche = {
  title: '', address: '', city: '', postal_code: '', region: '',
  consultation_phone: '', consultation_hours: '', specialties: [],
  bio: '', website: '', doctolib_url: '', parking_info: '', parking_address: '',
  latitude: null, longitude: null, accepts_new_patients: true,
  show_contact_button: true, visible: true,
};

const MesFichesPage = () => {
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyFiche });
  const [showForm, setShowForm] = useState(false);
  const [newSpec, setNewSpec] = useState('');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const isActive = user?.subscription_status === 'active' || user?.role === 'super_admin';

  useEffect(() => {
    fetchFiches();
    // Fresh status: a HelloAsso payment may have just reactivated the adhesion
    axios.get(`${API}/auth/me`).then(r => {
      setUser(r.data);
      localStorage.setItem('user', JSON.stringify({ ...JSON.parse(localStorage.getItem('user') || '{}'), ...r.data }));
    }).catch(() => {});
  }, []);

  // Geocoding debounce
  useEffect(() => {
    if (addressQuery.length < 4) { setAddressSuggestions([]); return; }
    const timer = setTimeout(async () => {
      setGeocoding(true);
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressQuery)}.json?access_token=${MAPBOX_TOKEN}&country=fr&limit=5&types=address,place&language=fr`
        );
        const data = await res.json();
        if (data.features) {
          setAddressSuggestions(data.features.map(f => {
            const ctx = f.context || [];
            const getCtx = (type) => (ctx.find(c => c.id?.startsWith(type)) || {}).text || '';
            return {
              full: f.place_name,
              address: f.text + (f.address ? ` ${f.address}` : ''),
              street: f.address ? `${f.address} ${f.text}` : f.text,
              city: getCtx('place') || getCtx('locality'),
              postal_code: getCtx('postcode'),
              region: getCtx('region'),
              lat: f.center[1],
              lng: f.center[0],
            };
          }));
          setShowAddressSuggestions(true);
        }
      } catch { }
      setGeocoding(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [addressQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-geocode-wrapper]')) setShowAddressSuggestions(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const selectAddress = (suggestion) => {
    // Match Mapbox region name to our REGIONS list
    const matchRegion = (mapboxRegion) => {
      if (!mapboxRegion) return '';
      const norm = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/-/g, ' ').toLowerCase().trim();
      const target = norm(mapboxRegion);
      return REGIONS.find(r => norm(r) === target || target.includes(norm(r)) || norm(r).includes(target)) || '';
    };

    setForm(f => ({
      ...f,
      address: suggestion.street || suggestion.address,
      city: suggestion.city || f.city,
      postal_code: suggestion.postal_code || f.postal_code,
      region: matchRegion(suggestion.region) || f.region,
      latitude: suggestion.lat,
      longitude: suggestion.lng,
    }));
    setAddressQuery('');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    toast.success('Adresse géolocalisée');
  };

  const fetchFiches = async () => {
    try {
      const res = await axios.get(`${API}/my-fiches`);
      setFiches(res.data);
    } catch { }
    setLoading(false);
  };

  const openNewForm = () => {
    setEditingId(null);
    setForm({ ...emptyFiche });
    setAddressQuery('');
    setAddressSuggestions([]);
    setShowForm(true);
  };

  const openEditForm = (fiche) => {
    setEditingId(fiche.id);
    setForm({ ...emptyFiche, ...fiche });
    setAddressQuery('');
    setAddressSuggestions([]);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.address || !form.city) {
      toast.error('Adresse et ville sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.put(`${API}/my-fiches/${editingId}`, form);
        setFiches(f => f.map(x => x.id === editingId ? res.data : x));
        toast.success('Fiche mise à jour');
      } else {
        const res = await axios.post(`${API}/my-fiches`, form);
        setFiches(f => [res.data, ...f]);
        toast.success('Fiche créée');
      }
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette fiche ?')) return;
    try {
      await axios.delete(`${API}/my-fiches/${id}`);
      setFiches(f => f.filter(x => x.id !== id));
      toast.success('Fiche supprimée');
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const addSpecialty = (s) => {
    const val = (s || newSpec).trim();
    if (val && !form.specialties.includes(val)) {
      setForm(f => ({ ...f, specialties: [...f.specialties, val] }));
      setNewSpec('');
    }
  };

  const parkingMapUrl = form.parking_address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(form.parking_address)}`
    : null;

  if (loading) return <div className="flex items-center justify-center min-h-full"><Loader2 size={32} className="animate-spin text-[#35AFE4]" /></div>;

  return (
    <div className="min-h-full bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="mes-fiches-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Mes Fiches Annuaire
            </h1>
            <p className="text-sm text-gray-500 mt-1">Gérez vos fiches de consultation visibles dans l'annuaire</p>
          </div>
          <button onClick={openNewForm} disabled={!isActive}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="new-fiche-btn">
            <Plus size={16} /> Nouvelle fiche
          </button>
        </div>

        {!isActive && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="fiches-locked-banner">
            <div className="flex items-start gap-3 flex-1">
              <Lock size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">Votre adhésion n'est pas active : vos fiches sont masquées de l'annuaire et leur gestion est suspendue. Elles redeviennent visibles automatiquement dès le paiement de votre cotisation.</p>
            </div>
            <Link to="/adhesion" className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 transition-colors flex-shrink-0" data-testid="fiches-renew-btn">
              Renouveler mon adhésion
            </Link>
          </div>
        )}

        {/* Fiches List */}
        {fiches.length === 0 && !showForm ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <MapPin size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-4">Aucune fiche créée</p>
            <button onClick={openNewForm} disabled={!isActive} className="text-sm text-[#35AFE4] font-semibold hover:underline disabled:opacity-40 disabled:no-underline">
              Créer ma première fiche
            </button>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            {fiches.map(fiche => (
              <div key={fiche.id} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`fiche-card-${fiche.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base font-bold text-[#1e3a5f]">{fiche.title || 'Cabinet'}</h3>
                      {fiche.visible && isActive ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><Eye size={10} /> Visible</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 flex items-center gap-1"><EyeOff size={10} /> Masquée</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                      {fiche.address && <span className="flex items-center gap-1"><MapPin size={12} /> {fiche.address}, {fiche.city}</span>}
                      {fiche.consultation_phone && <span className="flex items-center gap-1"><Phone size={12} /> {fiche.consultation_phone}</span>}
                      {fiche.consultation_hours && <span className="flex items-center gap-1"><Clock size={12} /> {fiche.consultation_hours}</span>}
                    </div>
                    {fiche.specialties?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {fiche.specialties.map((s, i) => (
                          <span key={i} className="px-2 py-0.5 bg-[#eaf6fc] text-[#35AFE4] text-[10px] font-semibold rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                    {fiche.parking_info && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                        <Car size={12} /> {fiche.parking_info}
                        {fiche.parking_address && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fiche.parking_address)}`}
                            target="_blank" rel="noopener noreferrer" className="text-[#35AFE4] hover:underline ml-1">Itinéraire</a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 ml-4">
                    <button onClick={() => openEditForm(fiche)} disabled={!isActive} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#35AFE4] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`edit-fiche-${fiche.id}`}>
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(fiche.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors" data-testid={`delete-fiche-${fiche.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6" data-testid="fiche-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {editingId ? 'Modifier la fiche' : 'Nouvelle fiche'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>

            <div className="space-y-5">
              {/* Title */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Nom du cabinet</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                  placeholder="Ex: Cabinet Ophtalmologie Paris Centre" data-testid="input-fiche-title" />
              </div>

              {/* Address fields */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
                  <Navigation size={12} className="inline mr-1" />Recherche d'adresse (géolocalisation auto)
                </label>
                <div className="relative" data-geocode-wrapper>
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#35AFE4]" />
                  <input
                    value={addressQuery}
                    onChange={e => setAddressQuery(e.target.value)}
                    onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                    className="w-full pl-10 pr-10 py-2.5 bg-[#eaf6fc] border border-[#35AFE4]/30 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none placeholder:text-[#35AFE4]/50"
                    placeholder="Tapez une adresse pour géolocaliser automatiquement..."
                    data-testid="input-geocode-address"
                  />
                  {geocoding && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#35AFE4] animate-spin" />}
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-56 overflow-y-auto" data-testid="geocode-suggestions">
                      {addressSuggestions.map((s, i) => (
                        <button key={i} type="button" onClick={() => selectAddress(s)}
                          className="w-full flex items-start gap-2.5 px-3.5 py-2.5 hover:bg-[#eaf6fc] transition-colors text-left border-b border-gray-50 last:border-0"
                          data-testid={`geocode-option-${i}`}>
                          <MapPin size={14} className="text-[#35AFE4] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{s.street || s.address}</p>
                            <p className="text-[11px] text-gray-400">{s.postal_code} {s.city} — {s.region}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {form.latitude && form.longitude && (
                  <p className="mt-1.5 text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    Géolocalisé : {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Adresse *</label>
                  <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    placeholder="15 Rue de la Santé" data-testid="input-fiche-address" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Ville *</label>
                  <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    placeholder="Paris" data-testid="input-fiche-city" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Code postal</label>
                  <input value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    placeholder="75013" data-testid="input-fiche-postal" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Région</label>
                  <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    data-testid="select-fiche-region">
                    <option value="">Sélectionner une région</option>
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Téléphone</label>
                  <input value={form.consultation_phone} onChange={e => setForm(f => ({ ...f, consultation_phone: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    placeholder="01 44 55 66 77" data-testid="input-fiche-phone" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Horaires</label>
                  <input value={form.consultation_hours} onChange={e => setForm(f => ({ ...f, consultation_hours: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    placeholder="Lundi-Vendredi : 9h-18h" data-testid="input-fiche-hours" />
                </div>
              </div>

              {/* Parking */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="text-sm font-bold text-[#1e3a5f] mb-3 flex items-center gap-2"><Car size={16} className="text-[#35AFE4]" /> Parking à proximité</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Nom / description</label>
                    <input value={form.parking_info} onChange={e => setForm(f => ({ ...f, parking_info: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                      placeholder="Parking Indigo Champs-Élysées" data-testid="input-fiche-parking-info" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Adresse du parking (pour itinéraire)</label>
                    <input value={form.parking_address} onChange={e => setForm(f => ({ ...f, parking_address: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                      placeholder="15 Rue de Berri, 75008 Paris" data-testid="input-fiche-parking-address" />
                  </div>
                </div>
                {parkingMapUrl && (
                  <a href={parkingMapUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs text-[#35AFE4] font-semibold hover:underline">
                    <ExternalLink size={12} /> Voir l'itinéraire vers le parking
                  </a>
                )}
              </div>

              {/* Specialties */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Spécialités</label>
                <div className="flex gap-2 mb-2">
                  <input value={newSpec} onChange={e => setNewSpec(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSpecialty(); } }}
                    className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                    placeholder="Ajouter une spécialité..." data-testid="input-fiche-spec" />
                  <button onClick={() => addSpecialty()} className="px-3 py-2 bg-[#35AFE4] text-white rounded-lg"><Plus size={16} /></button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.specialties.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#eaf6fc] text-[#35AFE4] text-xs font-semibold rounded-full">
                      {s} <button onClick={() => setForm(f => ({ ...f, specialties: f.specialties.filter(x => x !== s) }))}><X size={11} /></button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.visible} onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} className="rounded" />
                  <span className="text-gray-700 font-medium">Visible dans l'annuaire</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.accepts_new_patients} onChange={e => setForm(f => ({ ...f, accepts_new_patients: e.target.checked }))} className="rounded" />
                  <span className="text-gray-700 font-medium">Accepte nouveaux patients</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">Annuler</button>
                <button onClick={handleSave} disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="save-fiche-btn">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {editingId ? 'Mettre à jour' : 'Créer la fiche'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MesFichesPage;
